import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider } from '@wix/design-system';
import {
  appMarketAppPageScrollPage,
  appMarketPageStartLoadedSrc24Evid163,
  appMarketPageView,
} from '@wix/bi-logger-app-market-data/v2';
import type { getAppPageDataResponse } from './app-page-api';
import { getAppPageDataById, getAppPageDataBySlug } from './app-page-api';
import { isAppWithBenefits } from '@wix/app-market-services';
import type { SupportedAppStatuses } from '@wix/app-market-services-types';
import {
  useEssentials,
  useJunkYard,
  useRouterContext,
  useServices,
  useSiteContext,
  QueryDecorator,
  AppMarketPage,
  useComponentLoadReporter,
  appPageId,
  initGoogleAnalytics,
  getAnalyticsParams,
  useTranslation,
  usePageLoad,
  getAppPageViewBiData,
} from '@wix/app-market-core';
import type { RoutePath, RoutePayloadMap } from '@wix/app-market-core-types';
import {
  AppPageSticky,
  VisibilityDetector,
  Notification,
} from '@wix/app-market-components';
import { useParallaxSticky } from './useParallelSticky';
import { AppNotification } from './app-page-sections/notifications/app-notification';
import { ReviewsSection } from './app-page-sections/reviews/reviews-section';
import { AppPageCTAs } from './app-page-ctas';
import { useQuickInfoActions } from './app-page-sections/quick-info/quick-info-actions';
import { AppPricingPlansSection } from './app-page-sections/pricing-plans';
import type { getReviewsSectionResponse } from './app-page-sections/reviews/reviews-api';
import { RecommendedForYouSection } from './app-page-sections/recommended-for-you';
import s from './app-page.scss';
import type { AppPageOptions } from './types';
import type { AppPageType } from '@wix/app-market-core-components-types';
import { appPageDataHooks } from './data-hooks';
import { useQueryClient } from '@tanstack/react-query';
import { AppPageSEO } from './app-page-seo';
import { isCompanyNameClickable } from './utils/company';
import { CenteredLoader } from '../../components/centered-loader';
import { BackButton } from '../../components/back-button';
import { AppPageInfo, LeanAppPageInfo } from './app-page-info';
import { withSaleBanner } from '../../components/sale-banner';
import { dataHooks as appPageInfoDataHooks } from './app-page-info/data-hooks';

export interface AppPageProps {
  id?: string;
  slug?: string;
  status?: SupportedAppStatuses;
  options?: AppPageOptions;
  appPageType: AppPageType;
}

export interface AppPageComponentProps extends getAppPageDataResponse {
  onChange: (reviews: getAppPageDataResponse['reviews']) => void;
  onLoad: () => void;
  status?: SupportedAppStatuses;
  options?: AppPageOptions;
  appPageType: AppPageType;
}

export function AppPageComponent({
  app,
  managedApp,
  notification,
  reviews,
  overview,
  quickInfo,
  properties,
  companyInfo,
  onChange,
  onLoad,
  pricingPlans,
  status = 'PUBLISHED',
  options = {},
  traceableTrader,
  permissions,
  appPageType,
}: AppPageComponentProps) {
  useMemo(() => onLoad(), [app.id]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { onCompanyClick, onBack: onBackAction } =
    useQuickInfoActions(appPageType);
  const { t } = useTranslation();
  const { biLogger, experiments, googleAnalytics } = useEssentials();
  const { metaSiteId, isPremium, languageCode } = useSiteContext();
  const { router, route } = useRouterContext();
  const { routerData, goBackToReferrer } = useJunkYard();
  const [shouldShowHeader, setShouldShowHeader] = useState<boolean>(false);
  const onBack = router.hasHistory() ? () => onBackAction(app.id) : undefined;
  const { searchLocation } =
    route.payload as RoutePayloadMap[RoutePath.APP_PAGE];

  const {
    showRecommendedForYou = true,
    showHeaderLinks = true,
    stickyHeaderOptions,
    pricingOptions,
    showReviews = true,
    showStickyHeader = true,
    showPricingPlans = true,
    mode,
    disabledLinks = [],
    showAppPageLink = true,
  } = options;
  const isSlimExperimentEnabled = Boolean(
    experiments?.enabled('spec.app-market.OneStepInstallationSlimUI'),
  );
  const isMinimalSkin = options?.skin === 'lean' || options?.skin === 'slim';
  const isLeanSkin = options?.skin === 'lean';
  const isNewSlimSkin = options?.skin === 'slim' && isSlimExperimentEnabled;
  const isGalleryParallexScrolling = isNewSlimSkin && !notification;

  const { setSticky, unsetSticky } = useParallaxSticky({
    scrollContainerRef,
    dataHookSelector: `[data-hook="${appPageInfoDataHooks.leanAppPageInfoAppGallery}"]`,
    cssVarName: '--gallery-top',
    shouldEnable: isGalleryParallexScrolling,
    parallaxMultiplier: 1,
    maxTop: 70,
    stickyPadding: 0,
  });

  usePageLoad({
    route,
    oldRoute: routerData.route,
    onLoad: () => onAppPageLoad({ app, reviews, properties }),
  });

  useEffect(() => {
    const gaInstance = initGoogleAnalytics(googleAnalytics, app.gTag);
    const handlePageLoadAnalytics = async () => {
      const { referral, referralSectionName, appIndex } =
        (route.payload as RoutePayloadMap[RoutePath.APP_PAGE]) || {};
      gaInstance?.reportPageView({
        params: getAnalyticsParams({
          referral,
          referralSectionName,
          appIndex,
        }),
      });
    };
    handlePageLoadAnalytics();

    return () => {
      const isGoogleAnalyticsEnabled = experiments.enabled(
        'specs.app-market.enableGoogleAnalytics',
      );
      if (isGoogleAnalyticsEnabled) {
        googleAnalytics.reset();
      }
    };
  }, [app]);

  const onSectionFirstVisible = ({
    sectionName,
    sectionIndex,
  }: {
    sectionName: string;
    sectionIndex: number;
  }) => {
    biLogger.report(
      appMarketAppPageScrollPage({
        app_id: app.id,
        page_part: sectionName,
        page_part_index: sectionIndex,
        tag_name: 'app_page',
        app_page_type: appPageType,
      }),
    );
  };

  const onAppPageLoad = (props: {
    app: getAppPageDataResponse['app'];
    reviews: getAppPageDataResponse['reviews'];
    properties: getAppPageDataResponse['properties'];
  }) => {
    const searchTerm = location.search.split('referralTag=')?.[1];
    biLogger.report(
      appMarketPageView(
        getAppPageViewBiData({
          routerData,
          pageType: AppMarketPage.APP_PAGE,
          appId: props.app.id,
          appSlug: props.app.slug,
          appReviewCount: props.reviews.reviewsSummary.totalReviews,
          appReviewScore: props.reviews.reviewsSummary.averageRating,
          numberOfDependedApps: props.properties.appDependencies.length,
          searchTerm,
          mode:
            mode === 'test'
              ? 'test_my_app'
              : mode === 'unlisted'
              ? 'unlisted'
              : 'public',
          appBadges: props.app.appBadges,
          referrerNameForBI: goBackToReferrer?.referrerNameForBI,
          searchLocation,
          appPageType,
        }),
      ),
    );
  };

  return (
    <div data-hook={appPageDataHooks.APP_PAGE_COMPONENT}>
      {showStickyHeader && (
        <AppPageSticky
          isVisible={shouldShowHeader}
          icon={app.icon}
          mode={mode}
          company={companyInfo}
          installation={app.installation}
          pricing={app.pricing}
          appBadges={app.appBadges}
          reviews={
            isNewSlimSkin
              ? {
                  averageRating: reviews.reviewsSummary.averageRating,
                  totalCount: reviews.reviewsSummary.totalReviews,
                }
              : {}
          }
          onBack={onBack}
          mainActionButton={
            <AppPageCTAs
              managedApp={managedApp}
              status={status}
              app={{
                id: app.id,
                instanceId: app.instanceId,
                name: app.name,
                isUpgradable: app.isUpgradable,
                isAppWithBenefits: isAppWithBenefits(app.appBadges),
                isInstalled: app.installation?.isInstalled ?? false,
                isBuiltIn: Boolean(app.installation.isBuiltIn),
                appBadges: app.appBadges,
                notification,
                gTag: app.gTag,
                externalPricingPageUrl: pricingPlans?.externalPricingPageUrl,
              }}
              size="small"
              dataHook={appPageDataHooks.APP_STICKY_HEADER_CTA}
              reverseOrder
              isTestMode={mode === 'test'}
              isPermissionsRequired={!!permissions?.length}
              appPageType={appPageType}
            />
          }
          onCompanyClick={
            isCompanyNameClickable({
              disabledLinks,
              status,
            })
              ? (companyName: string, companySlug: string) =>
                  onCompanyClick(companyName, companySlug, app.id)
              : undefined
          }
          name={app.name}
          isAppUpgradable={app.isUpgradable}
          languageCode={languageCode}
          experiments={experiments}
          isPremiumSite={isPremium}
          options={stickyHeaderOptions}
        />
      )}
      {mode === 'test' && (
        <Box
          position="relative"
          display="block"
          marginBottom="60px"
          zIndex={100}
        >
          <Notification
            show
            actionButtonType="textLink"
            label={t('solution.page.Notification.TextLabel', {
              appName: app.name,
            })}
            labelActionButton={t('solution.page.Notification.ActionButton')}
            onClickActionButton={() =>
              window.open(
                t('solution.page.Notification.ActionButton.link'),
                '_blank',
              )
            }
          />
        </Box>
      )}

      <div
        className={`${s.appPage} ${
          isNewSlimSkin ? s.slim : isMinimalSkin ? s.lean : ''
        }`}
      >
        <Box
          direction="vertical"
          className={`${isNewSlimSkin || isLeanSkin ? s.container : ''}`}
          ref={scrollContainerRef}
        >
          {showHeaderLinks && (
            <Box marginTop="18px">
              {onBack && mode !== 'test' && mode !== 'unlisted' && (
                <Box dataHook={appPageDataHooks.BACK_BUTTON}>
                  <BackButton
                    size="tiny"
                    weight="thin"
                    skin="dark"
                    onBack={onBack}
                  />
                </Box>
              )}
            </Box>
          )}

          {notification ? (
            <Box
              className={`${s.notification} ${
                isNewSlimSkin ? s.newSlim : isMinimalSkin ? s.minimalSkin : ''
              }`}
            >
              <AppNotification
                notification={notification}
                appPageType={appPageType}
              />
            </Box>
          ) : null}

          {isMinimalSkin ? (
            <LeanAppPageInfo
              app={app}
              overview={overview}
              quickInfo={quickInfo}
              permissions={permissions}
              showNotificationGap={!!notification}
              isSlim={options?.skin === 'slim'}
              onShowHeader={
                isNewSlimSkin
                  ? () => {
                      if (shouldShowHeader) {
                        return;
                      }
                      setShouldShowHeader(true);
                      setSticky();
                    }
                  : undefined
              }
              onHideHeader={
                isNewSlimSkin
                  ? () => {
                      if (!shouldShowHeader) {
                        return;
                      }
                      setShouldShowHeader(false);
                      unsetSticky();
                    }
                  : undefined
              }
              showAppPageLink={showAppPageLink}
            />
          ) : (
            <AppPageInfo
              app={app}
              status={status}
              options={options}
              reviews={reviews}
              overview={overview}
              quickInfo={quickInfo}
              properties={properties}
              companyInfo={companyInfo}
              notification={notification}
              traceableTrader={traceableTrader}
              onSectionLoad={() =>
                onSectionFirstVisible({
                  sectionName: 'SolutionOverview',
                  sectionIndex: 1,
                })
              }
              onShowHeader={() => setShouldShowHeader(true)}
              isTestMode={mode === 'test'}
              onHideHeader={() => setShouldShowHeader(false)}
              externalPricingPageUrl={pricingPlans?.externalPricingPageUrl}
              appPageType={appPageType}
            />
          )}

          {showReviews && (
            <>
              <Box margin="36px 0px">
                <Divider dataHook="reviews-top-divider" />
              </Box>
              <VisibilityDetector
                onFirstVisible={() =>
                  onSectionFirstVisible({
                    sectionName: 'SolutionReviews',
                    sectionIndex: 2,
                  })
                }
              >
                <ReviewsSection
                  app={app}
                  company={companyInfo}
                  reviews={reviews}
                  onChange={onChange}
                />
              </VisibilityDetector>
            </>
          )}

          {showPricingPlans && pricingPlans && !pricingPlans.isFreeApp && (
            <>
              <Box margin="36px 0px">
                <Divider />
              </Box>

              <VisibilityDetector
                onFirstVisible={() =>
                  onSectionFirstVisible({
                    sectionName: 'SolutionPricingPlans',
                    sectionIndex: 3,
                  })
                }
              >
                <AppPricingPlansSection
                  {...pricingPlans}
                  options={pricingOptions}
                  appPageType={appPageType}
                />
              </VisibilityDetector>
            </>
          )}

          {showRecommendedForYou && metaSiteId && (
            <>
              <Box margin="36px 0px">
                <Divider />
              </Box>
              <VisibilityDetector
                onFirstVisible={() =>
                  onSectionFirstVisible({
                    sectionName: 'SolutionRelatedApps',
                    sectionIndex: 4,
                  })
                }
              >
                <RecommendedForYouSection appId={app.id} />
              </VisibilityDetector>
            </>
          )}
        </Box>
      </div>
    </div>
  );
}

const AppPageContainer = function ({
  id,
  slug,
  status = 'PUBLISHED',
  options,
  appPageType,
}: AppPageProps) {
  const siteContext = useSiteContext();
  const languageCode = siteContext.languageCode;
  const queryKey = `${appPageId}-${id || slug}-${languageCode}-${status}-${
    siteContext.isPremium
  }`;
  const reportLoadFinish = useComponentLoadReporter({
    componentId: appPageId,
    params: { appId: id, slug },
    deps: [queryKey],
  });
  const { biLogger, experiments } = useEssentials();
  const queryClient = useQueryClient();
  const services = useServices();
  const [reviews, setReviews] = useState<
    getReviewsSectionResponse | undefined
  >();

  useEffect(() => {
    biLogger.report(
      appMarketPageStartLoadedSrc24Evid163({
        tag_type: AppMarketPage.APP_PAGE,
        app_page_type: appPageType,
      }),
    );
  }, [slug]);

  return (
    <QueryDecorator
      queryFn={() =>
        id
          ? getAppPageDataById({
              services,
              id,
              languageCode,
              siteContext,
              status,
              experiments,
            })
          : slug
          ? getAppPageDataBySlug({
              services,
              slug,
              languageCode,
              status,
              siteContext,
              experiments,
            })
          : Promise.reject(new Error('No id or slug provided'))
      }
      queryKey={queryKey}
    >
      {({ data, isLoading }) => {
        if (isLoading) {
          return <CenteredLoader />;
        }
        return (
          <>
            {!siteContext.metaSiteId && (
              <AppPageSEO
                description={data.app.shortDescription}
                metaTagsAggregator={siteContext.metaTagsAggregator ?? []}
                appName={data.app.name}
                reviewsSummary={data.reviews.reviewsSummary}
                icon={data.app.icon}
                hiddenAppCollections={data.properties.hiddenAppCollections}
              />
            )}
            <AppPageComponent
              {...data}
              options={options}
              reviews={reviews ?? data.reviews}
              onChange={(newReviews: getReviewsSectionResponse) => {
                setReviews(newReviews);
                queryClient.invalidateQueries([queryKey]);
              }}
              onLoad={reportLoadFinish}
              status={status}
              appPageType={appPageType}
            />
          </>
        );
      }}
    </QueryDecorator>
  );
};

const AppPageWithBanner = withSaleBanner(AppPageContainer);

export const AppPage = (props: AppPageProps) =>
  props.status === 'PUBLISHED' ? (
    <AppPageWithBanner {...props} />
  ) : (
    <AppPageContainer {...props} />
  );
